import { AlertCircle, AlertTriangle, Info } from 'lucide-react';
import { cn } from '../lib/cn';
import type { PerClassCount, SplitSummary, SplitWarning } from '../types/api';
import SplitChip from './SplitChip';

/**
 * G.6 — read-only table of per-class examples across train / val /
 * test + a CONSOLIDATED suggestions block underneath.
 *
 * Used in three places (same component, same wire shape, no branching):
 *   1. Lock modal — live, re-renders as customer adjusts ratio
 *   2. Post-lock confirmation modal — read-only snapshot
 *   3. Project header banner expand + Export modal — read-only from
 *      GET /snapshots/:id/split-summary
 *
 * Warning presentation choice (from the 2026-06-07 UX feedback):
 *   - DO NOT show the per-class "Only X/Y/Z..." messages — they
 *     duplicate the table numbers and stack up fast (3 classes × 2
 *     codes each = 6 lines of noise).
 *   - DO color-code the table cells: hard-block rows get a red
 *     background; soft cells (0 in val/test, <5 in any split) get
 *     red/amber numbers so the eye lands on the right cells.
 *   - DO consolidate the remaining warning copy into ONE compact
 *     "Suggestions" block, grouping multiple classes that share the
 *     same issue ("orange, banana: no testing examples").
 *
 * Customer-facing vocab throughout: "label", "examples",
 * "training / validation / testing". No "class", "stratified",
 * "k-fold", etc. — those go in code comments only.
 */
interface ClassCoveragePreviewProps {
    summary: SplitSummary | null;
    loading?: boolean;
    isDarkMode?: boolean;
    /** When false, hides the explanation paragraph and the strategy /
     *  ratio header — useful in tighter surfaces (project header
     *  expansion). The table + suggestions stay the same. */
    showHeader?: boolean;
}

export default function ClassCoveragePreview({
    summary,
    loading = false,
    isDarkMode = false,
    showHeader = true,
}: ClassCoveragePreviewProps) {
    const cardCls = cn(
        'rounded-lg border',
        isDarkMode ? 'border-gray-700 bg-gray-900/30' : 'border-gray-200 bg-white',
    );
    const subtleCls = isDarkMode ? 'text-gray-400' : 'text-gray-500';
    const headingCls = cn(
        'text-xs font-semibold uppercase tracking-wide',
        isDarkMode ? 'text-gray-300' : 'text-gray-600',
    );

    if (loading && !summary) {
        return (
            <div className={cn(cardCls, 'p-4 text-sm', subtleCls)}>
                Calculating dataset split…
            </div>
        );
    }
    if (!summary) {
        return (
            <div className={cn(cardCls, 'p-4 text-sm', subtleCls)}>
                No split data available.
            </div>
        );
    }

    const { counts_per_class, global, warnings, strategy, ratio } = summary;

    // Group warnings by code → list of class names that share that code.
    // Codes we surface; intentionally drop "fewer_than_five_in_split"
    // (the table cells convey this visually with color).
    const grouped = groupWarnings(warnings);

    return (
        <div className={cn(cardCls, 'overflow-hidden')}>
            {showHeader && (
                <div className={cn(
                    'px-4 py-3 border-b',
                    isDarkMode ? 'border-gray-700 bg-gray-800/40' : 'border-gray-200 bg-gray-50',
                )}>
                    <div className="flex items-baseline justify-between gap-3 flex-wrap">
                        <span className={headingCls}>Examples per label in each set</span>
                        <span className={cn('text-[11px] flex items-center gap-1.5 flex-wrap', subtleCls)}>
                            <span>{strategy === 'stratified' ? 'Stratified by label' : 'Random'}</span>
                            <span>·</span>
                            <SplitChip split="train" size="xs" isDarkMode={isDarkMode} />
                            <span>{ratio.train}%</span>
                            <SplitChip split="val" size="xs" isDarkMode={isDarkMode} />
                            <span>{ratio.val}%</span>
                            <SplitChip split="test" size="xs" isDarkMode={isDarkMode} />
                            <span>{ratio.test}%</span>
                        </span>
                    </div>
                    <p className={cn('mt-1.5 text-xs', subtleCls)}>
                        Each label needs enough examples in every set for the model to learn
                        it and to measure accuracy.
                    </p>
                </div>
            )}

            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className={cn(
                        'text-xs uppercase tracking-wide',
                        isDarkMode ? 'text-gray-400 bg-gray-800/30' : 'text-gray-500 bg-gray-50',
                    )}>
                        <tr>
                            <th className="text-left px-4 py-2 font-medium">Label</th>
                            <th className="text-right px-3 py-2 font-medium">Training</th>
                            <th className="text-right px-3 py-2 font-medium">Validation</th>
                            <th className="text-right px-3 py-2 font-medium">Testing</th>
                            <th className="text-right px-4 py-2 font-medium">Total</th>
                        </tr>
                    </thead>
                    <tbody className={cn(
                        isDarkMode ? 'divide-gray-800' : 'divide-gray-100',
                        'divide-y',
                    )}>
                        {counts_per_class.length === 0 && (
                            <tr>
                                <td colSpan={5} className={cn('px-4 py-3 text-center', subtleCls)}>
                                    No labels yet — add classes and annotations first.
                                </td>
                            </tr>
                        )}
                        {counts_per_class.map((row) => (
                            <CoverageRow
                                key={row.class_index}
                                row={row}
                                isDarkMode={isDarkMode}
                                rowHasHardWarning={warnings.some((w) => w.hard && w.class_index === row.class_index)}
                            />
                        ))}
                        {counts_per_class.length > 0 && (
                            <tr className={cn(
                                'font-semibold',
                                isDarkMode ? 'bg-gray-800/40 text-gray-200' : 'bg-gray-50 text-gray-800',
                            )}>
                                <td className="px-4 py-2">All images</td>
                                <td className="px-3 py-2 text-right tabular-nums">{global.train}</td>
                                <td className="px-3 py-2 text-right tabular-nums">{global.val}</td>
                                <td className="px-3 py-2 text-right tabular-nums">{global.test}</td>
                                <td className="px-4 py-2 text-right tabular-nums">{global.total}</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Consolidated suggestions — one short bullet per issue type,
                naming all classes that share it. Skipped entirely when
                there's nothing to surface. */}
            {grouped.length > 0 && (
                <div className={cn(
                    'px-4 py-3 border-t space-y-1.5 text-xs',
                    isDarkMode ? 'border-gray-700 bg-gray-800/30' : 'border-gray-200 bg-gray-50',
                )}>
                    <div className={cn(
                        'flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide',
                        isDarkMode ? 'text-gray-300' : 'text-gray-600',
                    )}>
                        <Info className="h-3 w-3" />
                        Suggestions
                    </div>
                    {grouped.map((g) => (
                        <div
                            key={g.code}
                            className={cn(
                                'flex items-start gap-1.5',
                                g.hard
                                    ? (isDarkMode ? 'text-red-300' : 'text-red-700')
                                    : (isDarkMode ? 'text-amber-300' : 'text-amber-700'),
                            )}
                        >
                            {g.hard ? (
                                <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                            ) : (
                                <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                            )}
                            <span>
                                <span className="font-medium">{g.classes.join(', ')}</span>
                                {': '}
                                {g.message}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

interface CoverageRowProps {
    row: PerClassCount;
    isDarkMode: boolean;
    rowHasHardWarning: boolean;
}

/**
 * One row of the coverage table — colored cells when a number is
 * suspiciously low (0 → red, <5 → amber) so the eye finds the
 * problems without reading text.
 */
function CoverageRow({ row, isDarkMode, rowHasHardWarning }: CoverageRowProps) {
    const baseCellCls = isDarkMode ? 'text-gray-300' : 'text-gray-700';

    /** Picks a per-cell color for split-bucket counts based on how
     *  rare the count is. Train cells aren't color-coded — they're
     *  the bulk bucket so low train is captured by the whole-row hard
     *  warning instead. */
    const cellCls = (n: number, isTrain: boolean) => {
        if (isTrain) return baseCellCls;
        if (n === 0) return isDarkMode ? 'text-red-300 font-semibold' : 'text-red-700 font-semibold';
        if (n < 5) return isDarkMode ? 'text-amber-300' : 'text-amber-700';
        return baseCellCls;
    };

    return (
        <tr
            className={cn(
                rowHasHardWarning && (isDarkMode ? 'bg-red-900/20' : 'bg-red-50'),
            )}
        >
            <td className="px-4 py-2">
                <div className="flex items-center gap-1.5">
                    <span className={isDarkMode ? 'text-gray-200' : 'text-gray-800'}>
                        {row.class_name}
                    </span>
                    {rowHasHardWarning && (
                        <AlertCircle className="h-3.5 w-3.5 text-red-500 shrink-0" />
                    )}
                </div>
            </td>
            <td className={cn('px-3 py-2 text-right tabular-nums', cellCls(row.train, true))}>
                {row.train}
            </td>
            <td className={cn('px-3 py-2 text-right tabular-nums', cellCls(row.val, false))}>
                {row.val}
            </td>
            <td className={cn('px-3 py-2 text-right tabular-nums', cellCls(row.test, false))}>
                {row.test}
            </td>
            <td className={cn(
                'px-4 py-2 text-right tabular-nums font-medium',
                isDarkMode ? 'text-gray-200' : 'text-gray-800',
            )}>
                {row.total}
            </td>
        </tr>
    );
}

interface GroupedWarning {
    code: string;
    hard: boolean;
    message: string;
    classes: string[];
}

/**
 * groupWarnings — collapse the per-class warning array into one
 * row per issue type, naming every class that shares that code.
 *
 * Drops "fewer_than_five_in_split" entirely — that signal lives in
 * the table cell coloring now, which is denser and clearer than a
 * sentence per class.
 */
function groupWarnings(warnings: SplitWarning[]): GroupedWarning[] {
    const buckets = new Map<string, { hard: boolean; classes: string[] }>();
    for (const w of warnings) {
        if (w.code === 'fewer_than_five_in_split') continue;
        const entry = buckets.get(w.code);
        if (entry) {
            entry.classes.push(w.class_name);
        } else {
            buckets.set(w.code, { hard: w.hard, classes: [w.class_name] });
        }
    }
    const out: GroupedWarning[] = [];
    for (const [code, { hard, classes }] of buckets) {
        out.push({
            code,
            hard,
            classes,
            message: messageFor(code, classes.length),
        });
    }
    // Hard issues first, then by code for deterministic order.
    out.sort((a, b) => {
        if (a.hard !== b.hard) return a.hard ? -1 : 1;
        return a.code.localeCompare(b.code);
    });
    return out;
}

/**
 * messageFor — actionable, customer-facing copy keyed off the
 * backend's warning code. Singular/plural sensitive.
 */
function messageFor(code: string, classCount: number): string {
    const labelWord = classCount === 1 ? 'this label' : 'these labels';
    switch (code) {
        case 'no_images':
            return 'No images in the project yet — add images before locking.';
        case 'zero_train_instances':
            return `No training examples — the model can't learn ${labelWord}. Add more annotations or remove the label.`;
        case 'fewer_than_three_total':
            return `Fewer than 3 examples total — can't be split across training, validation, and testing. Add more examples or remove ${labelWord}.`;
        case 'zero_val_instances':
            return classCount === 1
                ? 'No validation examples — accuracy can\'t be tracked during training for this label.'
                : 'No validation examples — accuracy can\'t be tracked during training for these labels.';
        case 'zero_test_instances':
            return classCount === 1
                ? 'No testing examples — final accuracy can\'t be measured for this label.'
                : 'No testing examples — final accuracy can\'t be measured for these labels.';
        default:
            return 'See split breakdown above for details.';
    }
}
